import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, ExternalLink } from "lucide-react";

export default function AdminTransactions() {
  const [rows, setRows] = useState([]);
  const [retrying, setRetrying] = useState(null);
  const navigate = useNavigate();
  const load = async () => { const { data } = await api.get("/admin/transactions"); setRows(data); };
  useEffect(() => { load(); }, []);

  const openBooking = async (t) => {
    if (t.booking_id) { navigate(`/admin/bookings/${t.booking_id}`); return; }
    // Fall back to the FAxxxxxx → booking-id resolver
    try {
      const r = await api.get(`/admin/bookings/by-internal-ref/${encodeURIComponent(t.internal_ref || t.transaction_id || "")}`);
      navigate(`/admin/bookings/${r.data.id}`);
    } catch { toast.error("Cannot open booking — record not found"); }
  };

  const retryIssuance = async (t) => {
    if (!t.booking_id) { toast.error("Cannot retry — booking reference missing"); return; }
    setRetrying(t.transaction_id);
    try {
      const { data } = await api.post(`/admin/bookings/${t.booking_id}/retry-issuance`);
      if (data?.error_messages?.length) toast.error(data.error_messages.join(" | "));
      else if (data?.segment_errors?.length) toast.error(data.segment_errors[0].message);
      else toast.success(`Retried — PNR ${data?.carrier_locator || "—"}, Tickets ${(data?.ticket?.tickets || []).length}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(null);
    }
  };

  const pill = (v, kind = "default") => {
    if (!v) return <Badge className="bg-slate-100 text-slate-700 border-0">—</Badge>;
    const palettes = {
      pay:  { paid: "bg-emerald-100 text-emerald-700", unpaid: "bg-amber-100 text-amber-700", failed: "bg-red-100 text-red-700" },
      book: { confirmed: "bg-emerald-100 text-emerald-700", cancelled: "bg-red-100 text-red-700",
              pending_payment: "bg-amber-100 text-amber-700", pending: "bg-amber-100 text-amber-700",
              failed: "bg-red-100 text-red-700" },
      tick: { issued: "bg-emerald-100 text-emerald-700", cancelled: "bg-red-100 text-red-700",
              pending: "bg-amber-100 text-amber-700", failed: "bg-red-100 text-red-700" },
    };
    const p = palettes[kind] || {};
    return <Badge className={`${p[v] || "bg-slate-100 text-slate-700"} border-0 capitalize`}>{String(v).replace(/_/g," ")}</Badge>;
  };

  return (
    <div>
      <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Transactions</h1>
      <p className="text-slate-500 mt-1">Enriched view: pax, PNR, supplier, airline and statuses.</p>

      <Card className="mt-5 overflow-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Txn ID</TableHead><TableHead>Date</TableHead><TableHead>Pax Name</TableHead><TableHead>PNR</TableHead>
            <TableHead>Ticket #</TableHead><TableHead>Supplier</TableHead><TableHead>Airline</TableHead>
            <TableHead>Issuance</TableHead><TableHead>API Payment</TableHead><TableHead>Booking</TableHead>
            <TableHead>Ticketing</TableHead><TableHead>Amount</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map(t => (
              <TableRow key={t.transaction_id} data-testid={`txn-row-${t.transaction_id}`}>
                <TableCell>
                  <button
                    onClick={() => openBooking(t)}
                    className="font-mono text-xs font-bold text-[var(--brand-primary-dark)] hover:text-[#0f766e] hover:underline underline-offset-2 inline-flex items-center gap-1"
                    data-testid={`txn-open-${t.transaction_id}`}
                    title="Open booking details"
                  >
                    {t.transaction_id} <ExternalLink className="w-3 h-3 opacity-70" />
                  </button>
                </TableCell>
                <TableCell className="text-xs text-slate-500">{t.transaction_date ? new Date(t.transaction_date).toLocaleString() : "—"}</TableCell>
                <TableCell className="font-medium">{t.pax_name}</TableCell>
                <TableCell className="font-mono text-xs">{t.pnr || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{t.ticket_no || "—"}</TableCell>
                <TableCell className="capitalize">{t.supplier}</TableCell>
                <TableCell>{t.airline || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{t.issuance_date ? new Date(t.issuance_date).toLocaleString() : "—"}</TableCell>
                <TableCell>{pill(t.api_payment_status, "pay")}</TableCell>
                <TableCell>
                  <span title={t.failure_reason || ""}>{pill(t.booking_status, "book")}</span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <span title={t.failure_reason || ""}>{pill(t.ticketing_voucher_status, "tick")}</span>
                    {(t.ticketing_voucher_status === "failed" || t.booking_status === "failed") && (
                      <Button
                        size="sm" variant="ghost" className="h-6 px-2 text-[10px]"
                        disabled={retrying === t.transaction_id}
                        onClick={() => retryIssuance(t)}
                        data-testid={`txn-retry-${t.transaction_id}`}
                      >
                        <RefreshCw className={`w-3 h-3 mr-1 ${retrying === t.transaction_id ? "animate-spin" : ""}`} /> Retry
                      </Button>
                    )}
                  </div>
                </TableCell>
                <TableCell className="font-semibold">${Number(t.amount).toFixed(2)} {t.currency?.toUpperCase()}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={12} className="text-center text-slate-400 py-10">No transactions yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
